import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * Cadillac Optiq (linkId 2366).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2366.ts`; shared UI boilerplate
 * (staticTranslations / dealers / saveFormConfig / externalScript) follows
 * 2378. `styleConfig` reuses the Cadillac Gothic palette/face verbatim from 2384.
 *
 * PARTIAL ASSET GAP, same root cause as Yukon/Lyriq: probed all 112 unique
 * 1.0 image references individually against the deployed host. The
 * legacy '2025 Luxury' trim's 11 exterior colours, 4 interior colours and
 * 10 feature stills (25 refs) don't resolve under any extension — every
 * OTHER reference, including every 2025 Luxury ACCESSORY and all three
 * 2026 trims in full, resolves cleanly. Missing refs left as empty
 * strings (MISSING_2025_LUXURY set above), not fabricated paths.
 * 
 * 2026 OPTIQ-V (the default variant) carries this project's only two
 * packages — Santorini Blue Package and Carbon Fiber Package — each
 * toggling a single accessory.
 *
 * 1.0 ships a REAL modelyear filter here (2025/2026, 2026 checked by
 * default) — same mechanism as Tahoe's and Lyriq's existing modelyear
 * filters. Kept, wired via a `modelyear` field on each variant, and
 * `toggleTabs.CONFIG` is left VISIBLE because of it.
 *
 * Assets live in public/assets/{images,videos}/2366/, resolved by
 * `assetSrc()` in src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Optiq: {
      variants: {
        '2025-luxury': {
          configurationId: '5vlpab0glq63',
          name: {
            en: '2025 Luxury',
            ar: 'فئة الفخامة 2025',
          },
          description: {
            en: 'AWD',
            ar: 'دفع رباعي',
          },
          drivetrain: 'AWD',
          trim: '2025 Luxury',
          highlights: [
            {
              en: '315 HP',
              ar: '315 حصان',
            },
            {
              en: 'Torque 450 NM',
              ar: 'عزم الدوران 450 نيوتن متر',
            },
            {
              en: 'Range 511 KM',
              ar: 'المدى 511 كم',
            },
          ],
          specs: [
            {
              en: '315 HP',
              ar: '315 حصان',
            },
            {
              en: '450 NM',
              ar: '450 نيوتن متر',
            },
          ],
          image: 'images/2366/OPTIQ_2025_Variant_Thumbnail.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0,
            KSA: 0,
            KW: 0,
            BHD: 0,
            LBP: 0,
            OMR: 0,
            QAR: 0,
            default: 0.1,
          },
          index: 0,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          modelyear: '2025',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: false,
              desktop: false,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SC',
          brandItemCode: 'CAOP01',
        },
        '2026-optiq-v': {
          configurationId: '5vlpabtt0glq63',
          name: {
            en: '2026 OPTIQ - V',
            ar: '2026 OPTIQ-V',
          },
          description: {
            en: 'AWD',
            ar: 'دفع رباعي',
          },
          drivetrain: 'AWD',
          trim: '2026 OPTIQ - V',
          highlights: [
            {
              en: '315 HP',
              ar: '315 حصان',
            },
            {
              en: 'Torque 450 NM',
              ar: 'عزم الدوران 450 نيوتن متر',
            },
            {
              en: 'Range 511 KM',
              ar: 'المدى 511 كم',
            },
          ],
          specs: [
            {
              en: '315 HP',
              ar: '315 حصان',
            },
            {
              en: '450 NM',
              ar: '450 نيوتن متر',
            },
          ],
          image: 'images/2366/Optiq_2026_Variant_Thumbnail.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0,
            KSA: 0,
            KW: 0,
            BHD: 0,
            LBP: 0,
            OMR: 0,
            QAR: 0,
            default: 0.1,
          },
          index: 1,
          isDefault: true,
          ar: {
            usdz: '',
            enable: false,
          },
          modelyear: '2026',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '2SV',
          brandItemCode: 'CAOP03',
        },
        '2026-premium-luxury': {
          configurationId: '5vlpabttoi0glq63',
          name: {
            en: '2026 Premium Luxury',
            ar: 'فئة الفخامة المميزة 2026',
          },
          description: {
            en: 'AWD',
            ar: 'دفع رباعي',
          },
          drivetrain: 'AWD',
          trim: '2026 Premium Luxury',
          highlights: [
            {
              en: '315 HP',
              ar: '315 حصان',
            },
            {
              en: 'Torque 450 NM',
              ar: 'عزم الدوران 450 نيوتن متر',
            },
            {
              en: 'Range 511 KM',
              ar: 'المدى 511 كم',
            },
          ],
          specs: [
            {
              en: '315 HP',
              ar: '315 حصان',
            },
            {
              en: '450 NM',
              ar: '450 نيوتن متر',
            },
          ],
          image: 'images/2366/optiq_2026_Premium_Luxury.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0,
            KSA: 0,
            KW: 0,
            BHD: 0,
            LBP: 0,
            OMR: 0,
            QAR: 0,
            default: 0.1,
          },
          index: 2,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          modelyear: '2026',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SE',
          brandItemCode: 'CAOP02',
        },
        '2026-luxury': {
          configurationId: '5vllmttoi0glq63',
          name: {
            en: '2026 Luxury',
            ar: '2026 Luxury',
          },
          description: {
            en: 'RWD',
            ar: 'دفع خلفي',
          },
          drivetrain: 'RWD',
          trim: '2026 Luxury',
          highlights: [
            {
              en: '315 HP',
              ar: '315 حصان',
            },
            {
              en: 'Torque 450 NM',
              ar: 'عزم الدوران 450 نيوتن متر',
            },
            {
              en: 'Range 511 KM',
              ar: 'المدى 511 كم',
            },
          ],
          specs: [
            {
              en: '315 HP',
              ar: '315 حصان',
            },
            {
              en: '450 NM',
              ar: '450 نيوتن متر',
            },
          ],
          image: 'images/2366/optiq_2026_Luxury.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            LBP: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0,
            KSA: 0,
            KW: 0,
            BHD: 0,
            LBP: 0,
            OMR: 0,
            QAR: 0,
            default: 0.1,
          },
          index: 3,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          modelyear: '2026',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: false,
              desktop: false,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SC',
          brandItemCode: 'CAOP01',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
          title: {
            en: 'City Landscape 1',
            ar: 'غروب الشمس على الشاطئ',
          },
        },
        g0ZA968_RpUe: {
          image: 'images/2366/change_bg_2.webp',
          title: {
            en: 'City Landscape 2',
            ar: 'غروب الشمس على الشاطئ',
          },
        },
      },
      ambientLighting: {},
      colors: {
        exterior: {
          '10jb3cwz3ub7': {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          z3686mwtpyj7: {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2366/ext_colors/official/Black_Raven.jpg',
            usdz: 'silver',
            price: {
              KSA: 1179,
              UAE: 1157,
              OMR: 123,
              BHD: 119,
              QAR: 1144,
              JOD: 223,
              KW: 97,
              LBP: 314,
              IQD: 314,
              default: 1157,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          '0zrjd3lm53he': {
            label: {
              en: 'Crystal White Tricoat',
              ar: 'أبيض Crystal بثلاث طبقات',
            },
            image: 'images/2366/ext_colors/official/Crystal_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Crystal_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX22',
          },
          cq03qyp7gnby: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر Radiant بطبقتين',
            },
            image: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '10xy9i682nxc': {
            label: {
              en: 'Coastal Blue Metallic',
              ar: 'أزرق Coastal معدني',
            },
            image: 'images/2366/ext_colors/official/Coastal_Blue_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 3078,
              UAE: 3021,
              OMR: 320,
              BHD: 312,
              QAR: 2988,
              JOD: 583,
              KW: 254,
              LBP: 821,
              IQD: 821,
              default: 3021,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Coastal_Blue_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX24',
          },
          '37xytt0490j8': {
            label: {
              en: 'Celestial Metallic',
              ar: 'Celestial معدني',
            },
            image: 'images/2366/ext_colors/official/Celestial_Metallic.jpg',
            usdz: 'red_black',
            price: {
              KSA: 3078,
              UAE: 3021,
              OMR: 320,
              BHD: 312,
              QAR: 2988,
              JOD: 583,
              KW: 254,
              LBP: 821,
              IQD: 821,
              default: 3021,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Celestial_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJY',
            brandItemCode: 'EX25',
          },
          '536y2x49kxgq': {
            label: {
              en: 'Argent Silver Metallic with Black roof',
              ar: 'فضي Argent معدني مع سقف أسود',
            },
            image: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'silver_black',
            price: {
              KSA: 2590,
              UAE: 2542,
              OMR: 269,
              BHD: 262,
              QAR: 2514,
              JOD: 490,
              KW: 214,
              LBP: 691,
              IQD: 691,
              default: 2542,
            },
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX57',
          },
          '4zd4onnmg2y5': {
            label: {
              en: 'Crystal White Tricoat with Black roof',
              ar: 'أبيض Crystal بثلاث طبقات مع سقف أسود',
            },
            image: 'images/2366/ext_colors/official/Crystal_White_Tricoat.jpg',
            usdz: 'blue_black',
            price: {
              KSA: 8039,
              UAE: 7889,
              OMR: 836,
              BHD: 815,
              QAR: 7803,
              JOD: 1522,
              KW: 665,
              LBP: 2144,
              IQD: 2144,
              default: 7889,
            },
            index: 7,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Crystal_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX66',
          },
          '2phvl5sq89cx': {
            label: {
              en: 'Radiant Red Tintcoat with Black roof',
              ar: 'أحمر Radiant بطبقتين مع سقف أسود',
            },
            image: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white_black',
            price: {
              KSA: 8039,
              UAE: 7889,
              OMR: 836,
              BHD: 815,
              QAR: 7803,
              JOD: 1522,
              KW: 665,
              LBP: 2144,
              IQD: 2144,
              default: 7889,
            },
            index: 8,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX86',
          },
          '03495u2095u209': {
            label: {
              en: 'Coastal Blue Metallic with Black roof',
              ar: 'أزرق Coastal معدني مع سقف أسود',
            },
            image: 'images/2366/ext_colors/official/Coastal_Blue_Metallic.jpg',
            usdz: 'white_black',
            price: {
              KSA: 5669,
              UAE: 5563,
              OMR: 590,
              BHD: 574,
              QAR: 5503,
              JOD: 1073,
              KW: 469,
              LBP: 1512,
              IQD: 1512,
              default: 5563,
            },
            index: 9,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Coastal_Blue_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX63',
          },
          '2phv23423452l5sq89cx': {
            label: {
              en: 'Celestial Metallic with Black roof',
              ar: 'Celestial معدني مع سقف أسود',
            },
            image: 'images/2366/ext_colors/official/Celestial_Metallic.jpg',
            usdz: 'white_black',
            price: {
              KSA: 5669,
              UAE: 5563,
              OMR: 590,
              BHD: 574,
              QAR: 5503,
              JOD: 1073,
              KW: 469,
              LBP: 1512,
              IQD: 1512,
              default: 5563,
            },
            index: 10,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Celestial_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJY',
            brandItemCode: 'EX61',
          },
          '9951ydicoxbo': {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 11,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          dhj6dscvmgab: {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2366/ext_colors/official/Black_Raven.jpg',
            usdz: 'silver',
            price: {
              KSA: 1179,
              UAE: 1157,
              OMR: 123,
              BHD: 119,
              QAR: 1144,
              JOD: 223,
              KW: 97,
              LBP: 314,
              IQD: 314,
              default: 1157,
            },
            index: 12,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          pm2x0dyqhgt7: {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2366/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 13,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          mxstdl6cglti: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر Radiant بطبقتين',
            },
            image: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 14,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          ag08jtrpa26j: {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 15,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          r7nn5ntbszly: {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2366/ext_colors/official/Black_Raven.jpg',
            usdz: 'silver',
            price: {
              KSA: 1179,
              UAE: 1157,
              OMR: 123,
              BHD: 119,
              QAR: 1144,
              JOD: 223,
              KW: 97,
              LBP: 314,
              IQD: 314,
              default: 1157,
            },
            index: 16,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          ojc9ptszsqvf: {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2366/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 17,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          ywn6ec8h269z: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر Radiant بطبقتين',
            },
            image: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 18,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          x64x0w8ca6xy: {
            label: {
              en: 'Coastal Blue Metallic',
              ar: 'أزرق Coastal معدني',
            },
            image: 'images/2366/ext_colors/official/Coastal_Blue_Metallic.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 19,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Coastal_Blue_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX24',
          },
          esnofdor9647: {
            label: {
              en: 'Celestial Metallic',
              ar: 'Celestial معدني',
            },
            image: 'images/2366/ext_colors/official/Celestial_Metallic.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 20,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Celestial_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJY',
            brandItemCode: 'EX25',
          },
          '2fa06p9t7v8f': {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 21,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          '69xux2hlscm1': {
            label: {
              en: 'Black Raven',
              ar: 'أسود Raven',
            },
            image: 'images/2366/ext_colors/official/Black_Raven.jpg',
            usdz: 'silver',
            price: {
              KSA: 1179,
              UAE: 1157,
              OMR: 123,
              BHD: 119,
              QAR: 1144,
              JOD: 223,
              KW: 97,
              LBP: 314,
              IQD: 314,
              default: 1157,
            },
            index: 22,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Black_Raven.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX23',
          },
          '5xzh26yrdinw': {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2366/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 23,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          '2fbmj48vpgsi': {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر Radiant بطبقتين',
            },
            image: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 24,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '2fbmj4ff8vpgsi': {
            label: {
              en: 'Coastal Blue Metallic',
              ar: 'أزرق Coastal معدني',
            },
            image: 'images/2366/ext_colors/official/Coastal_Blue_Metallic.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 25,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Coastal_Blue_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJV',
            brandItemCode: 'EX24',
          },
          '2fbmffj4ff8vpgsi': {
            label: {
              en: 'Celestial Metallic',
              ar: 'Celestial معدني',
            },
            image: 'images/2366/ext_colors/official/Celestial_Metallic.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 26,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2366/ext_colors/official/Celestial_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJY',
            brandItemCode: 'EX25',
          },
        },
        interior: {
          '86cgiceo3f7n': {
            label: {
              en: 'Sky Cool Gray',
              ar: 'رمادي Sky Cool',
            },
            image: 'images/2366/int_colors/official/Sky_Cool_Gray.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Sky_Cool_Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EGT',
            brandItemCode: 'IN76',
          },
          '48za413x82621': {
            label: {
              en: 'Phantom Blue',
              ar: 'أزرق Phantom',
            },
            image: 'images/2366/int_colors/official/Phantom_Blue.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 4750,
              UAE: 4661,
              OMR: 494,
              BHD: 481,
              QAR: 4610,
              JOD: 899,
              KW: 393,
              LBP: 1267,
              IQD: 1267,
              default: 4661,
            },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Phantom_Blue.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EFG',
            brandItemCode: 'IN12',
          },
          '48za413x82622': {
            label: {
              en: 'Autumn Canyon',
              ar: 'Autumn Canyon',
            },
            image: 'images/2366/int_colors/official/Autumn_Canyon.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 4750,
              UAE: 4661,
              OMR: 494,
              BHD: 481,
              QAR: 4610,
              JOD: 899,
              KW: 393,
              LBP: 1267,
              IQD: 1267,
              default: 4661,
            },
            index: 2,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Autumn_Canyon.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EFP',
            brandItemCode: 'IN11',
          },
          i8gu23g9uev7: {
            label: {
              en: 'Noir',
              ar: 'Noir',
            },
            image: 'images/2366/int_colors/official/Noir.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 3,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Noir.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EGW',
            brandItemCode: 'IN09',
          },
          j1x49yhmrfs1: {
            label: {
              en: 'Sky Cool Gray',
              ar: 'رمادي Sky Cool',
            },
            image: 'images/2366/int_colors/official/Sky_Cool_Gray.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 4,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Sky_Cool_Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EGT',
            brandItemCode: 'IN76',
          },
          v4qpw0s0id1s: {
            label: {
              en: 'Noir',
              ar: 'Noir',
            },
            image: 'images/2366/int_colors/official/Noir.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 5,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Noir.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EGW',
            brandItemCode: 'IN09',
          },
          zgaapk0bjy1c: {
            label: {
              en: 'Sky Cool Gray',
              ar: 'رمادي Sky Cool',
            },
            image: 'images/2366/int_colors/official/Sky_Cool_Gray.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 6,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Sky_Cool_Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EGT',
            brandItemCode: 'IN76',
          },
          mip65q6evc31: {
            label: {
              en: 'Noir',
              ar: 'Noir',
            },
            image: 'images/2366/int_colors/official/Noir.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 7,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Noir.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EGW',
            brandItemCode: 'IN09',
          },
          dcfdkz0j4nst: {
            label: {
              en: 'Phantom Blue',
              ar: 'أزرق Phantom',
            },
            image: 'images/2366/int_colors/official/Phantom_Blue.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 4750,
              UAE: 4661,
              OMR: 494,
              BHD: 481,
              QAR: 4610,
              JOD: 899,
              KW: 393,
              LBP: 1267,
              IQD: 1267,
              default: 4661,
            },
            index: 8,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Phantom_Blue.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EFG',
            brandItemCode: 'IN12',
          },
          yzm9eaoguz90: {
            label: {
              en: 'Autumn Canyon',
              ar: 'Autumn Canyon',
            },
            image: 'images/2366/int_colors/official/Autumn_Canyon.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 4750,
              UAE: 4661,
              OMR: 494,
              BHD: 481,
              QAR: 4610,
              JOD: 899,
              KW: 393,
              LBP: 1267,
              IQD: 1267,
              default: 4661,
            },
            index: 9,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Autumn_Canyon.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EFP',
            brandItemCode: 'IN11',
          },
          '48zalpo67x82621': {
            label: {
              en: 'Sky Cool Gray Inteluxe',
              ar: 'Inteluxe باللون الرمادي Sky Cool',
            },
            image: 'images/2366/int_colors/official/Sky_Cool_Gray_Inteluxe.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 10,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Sky_Cool_Gray_Inteluxe.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'E4T',
            brandItemCode: 'IN78',
          },
          '48zalpo77x82621': {
            label: {
              en: 'Noir Inteluxe',
              ar: 'Inteluxe باللون Noir',
            },
            image: 'images/2366/int_colors/official/Noir_Inteluxe.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 11,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2366/int_colors/official/Noir_Inteluxe.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'E4S',
            brandItemCode: 'IN63',
          },
        },
      },
      features: {
        dm0m2aj1jvy3: {
          label: {
            en: 'Choreographed Exterior Led Lighting',
            ar: 'أضواء خارجية LED راقصة',
          },
          image: '',
          title: {
            en: 'Black painted Alumin. Wheels 21 inch ',
            ar: 'عجلات ألمنيوم قياس 21 بوصة مطلية بالأسود',
          },
          desc: {
            en: 'Available lighting choreography greets the owner upon arrival with an animated light sequence that flows from the grille, headlamps and door handles before finishing on the rear taillamps.',
            ar: 'تستقبل الإضاءة الراقصة المتاحة المالك عند اقترابه بتسلسل ضوئي متحرك يبدأ من الشبكة، ويمتد إلى المصابيح الأمامية ومقابض الأبواب، قبل أن يختتم عند المصابيح الخلفية.',
          },
          type: 'exterior',
        },
        '1cuzidmw9n3g': {
          label: {
            en: 'Lean Proportions',
            ar: 'أبعاد انسيابية',
          },
          image: '',
          title: {
            en: 'Lean Proportions',
            ar: 'أبعاد انسيابية',
          },
          desc: {
            en: 'OPTIQ’s lean proportions contribute to a nimble ride that complements the smooth, near-instant torque of its Ultium battery platform. Several innovative solutions, such as a vented rear spoiler, diffusers and other sculptural elements, allow aggressive car-like aerodynamic efficiency.',
            ar: 'تسهم أبعاد OPTIQ الانسيابية في منحها رشاقة على الطريق، تتكامل مع عزم الدوران السلس وشبه الفوري الذي توفره منصة بطارية Ultium. كما تعزز مجموعة من الحلول المبتكرة، مثل الجناح الخلفي المزود بفتحات تهوية وموزعات الهواء وغيرها من العناصر المنحوتة، كفاءتها الديناميكية الهوائية بطابع رياضي جريء.',
          },
          type: 'exterior',
        },
        '3siptpq2iqi9': {
          label: {
            en: 'Black Crystal Grille',
            ar: 'شبكة سوداء كريستالية',
          },
          image: '',
          title: {
            en: 'Black Crystal Grille',
            ar: 'شبكة سوداء كريستالية',
          },
          desc: {
            en: 'A black crystal grille establishes the face of OPTIQ, with an illuminated Cadillac crest and bold design elements. Refined linework draws the eye to unique visual elements complemented with sharp sculptural details in the grille as well as the tail and headlamps.',
            ar: 'ترسم الشبكة السوداء الكريستالية ملامح واجهة OPTIQ، وتتوسطها شارة كاديلاك المضيئة وعناصر تصميم جريئة. وتلفت الخطوط المتقنة الأنظار إلى تفاصيل بصرية فريدة، تكتمل بلمسات منحوتة وحادة في الشبكة والمصابيح الأمامية والخلفية.',
          },
          type: 'exterior',
        },
        lljiom38dn2i: {
          label: {
            en: 'Rear Quarter-Panel Detail',
            ar: 'تفاصيل اللوحة الجانبية الخلفية',
          },
          image: '',
          title: {
            en: 'Rear Quarter-Panel Detail',
            ar: 'تفاصيل اللوحة الجانبية الخلفية',
          },
          desc: {
            en: 'High-gloss black lower body components around the wheels and doors draw the viewer’s eye up and emphasize OPTIQ’s sleek design. A precision-patterned rear quarter-window graphic fades to the rear, strengthening the fastback effect of the low roofline.',
            ar: 'تلفت مكونات الهيكل السفلية باللون الأسود اللامع حول العجلات والأبواب الأنظار إلى الأعلى، وتبرز تصميم OPTIQ الانسيابي. كما يتلاشى نقش النافذة الجانبية الخلفية، المصمم بنمط دقيق، باتجاه الجزء الخلفي ليعزز طابع Fastback الذي يمنحه خط السقف المنخفض.',
          },
          type: 'exterior',
        },
        mpm1i9izpcfc: {
          label: {
            en: 'Panoramic Glass Roof And Black Roof',
            ar: 'سقف بانورامي زجاجي وسقف أسود',
          },
          image: '',
          title: {
            en: 'Panoramic Glass Roof And Black Roof',
            ar: 'سقف بانورامي زجاجي وسقف أسود',
          },
          desc: {
            en: 'The stunning panoramic glass roof enables a seamless transition from glass to sheet metal. The fixed full-glass roof stretches uninterrupted, welcoming natural light and providing an expansive view from every seat while maximizing headroom in the cabin. The available black roof provides exciting contrast, while a vented rear spoiler adds to appearance while allowing air to flow over the rear window, eliminating the need for a rear wiper.',
            ar: 'يحقق السقف البانورامي الزجاجي انتقالاً سلساً بين الزجاج وهيكل السيارة المعدني. ويمتد السقف الزجاجي الثابت بالكامل دون انقطاع، ليدخل الضوء الطبيعي ويوفر إطلالة واسعة من كل مقعد، مع تعزيز مساحة الرأس داخل المقصورة. ويمنح السقف الأسود المتاح تبايناً لافتاً، فيما يضفي الجناح الخلفي المزود بفتحات تهوية لمسة مميزة ويسمح بتدفق الهواء فوق النافذة الخلفية، ما يلغي الحاجة إلى ماسحة خلفية.',
          },
          type: 'exterior',
        },
        '0kweybqq8kf3': {
          label: {
            en: 'Innovative Materials',
            ar: 'مواد مبتكرة',
          },
          image: '',
          title: {
            en: 'Innovative Materials',
            ar: 'مواد مبتكرة',
          },
          desc: {
            en: 'OPTIQ introduces new style with its available PaperWood décor. PaperWood veneer is made up of equal parts of tulip wood and recycled newspaper. This surface creates a harmonious balance between nature and modernity – avant-garde meets accessible. Fabrics provide a pleasant, tactile touch and striking appearance. OPTIQ’s patterned accent fabric is woven from yarn made from 100% recycled materials.',
            ar: 'تقدّم OPTIQ أسلوباً جديداً مع خامة PaperWood المتوفرة. وتتكوّن خامة PaperWood من أجزاء متساوية من خشب التوليب والصحف المعاد تدويرها، لتوفّر سطحاً يجمع بتناغم بين الطبيعة والحداثة، حيث يلتقي التصميم الطليعي بسهولة الاستخدام. كما توفّر الأقمشة ملمساً مريحاً ومظهراً لافتاً، فيما يُنسج قماش الزخارف المميز في OPTIQ من خيوط مصنوعة من مواد معاد تدويرها بنسبة 100%.',
          },
          type: 'interior',
        },
        yygvqpfvv5rt: {
          label: {
            en: 'Dual-Zone Ambient Lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة',
          },
          image: '',
          title: {
            en: 'Dual-Zone Ambient Lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة',
          },
          desc: {
            en: 'Inside OPTIQ, light sets the tone. The 126-color dual-zone ambient lighting lets you shape the cabin’s atmosphere to match every mood – calm, bold, or anything in between. Subtle illumination lines flow across the interior, enhancing its sculpted surfaces and sense of spaciousness.',
            ar: 'داخل OPTIQ، تبدأ التجربة مع الإضاءة. تتيح لك الإضاءة المحيطية ثنائية المناطق، المتوفرة بـ126 لوناً، تصميم أجواء المقصورة بما يناسب كل لحظة، سواء كنت تفضل أجواءً هادئة أو جريئة أو ما بينهما. وتمتد خطوط الإضاءة بانسيابية عبر المقصورة، لتبرز تفاصيل تصميمها وتعزز الإحساس بالرحابة.',
          },
          type: 'interior',
        },
        kxtve76vtocg: {
          label: {
            en: 'Segment-Best Second-Row Legroom',
            ar: 'مساحة الأرجل في الصف الثاني الأفضل في فئتها',
          },
          image: '',
          title: {
            en: 'Segment-Best Second-Row Legroom',
            ar: 'مساحة الأرجل في الصف الثاني الأفضل في فئتها',
          },
          desc: {
            en: 'OPTIQ has segment-best rear legroom of 960mm making it a comfortable space to stretch out and enjoy the ride. Rear passengers also have two USB-C ports and vents for convenience.',
            ar: 'توفر OPTIQ أفضل مساحة للأرجل في المقاعد الخلفية ضمن فئتها، تبلغ 960 مم، لتمنح الركاب رحابة استثنائية وراحة أكبر في كل رحلة. كما زُوّد الركاب في المقاعد الخلفية بمنفذي USB-C وفتحات تهوية، لمزيد من الراحة.',
          },
          type: 'interior',
        },
        ejzw6sqj44xd: {
          label: {
            en: '33-Inch Diagonal Advanced Led Display',
            ar: 'شاشة LED متطورة بقياس قطري 33 بوصة',
          },
          image: '',
          title: {
            en: '33-Inch Diagonal Advanced Led Display',
            ar: 'شاشة LED متطورة بقياس قطري 33 بوصة',
          },
          desc: {
            en: 'The 33” diagonal advanced LED display flows across the dashboard in a continuous curve, uniting information, entertainment, and control in vivid clarity. Paired with the available full-color Head-Up Display, key details such as speed and navigation are projected directly onto the windshield – creating a single, intuitive interface that keeps every command within sight and every drive in focus.',
            ar: 'تمتد شاشة LED المتطورة بقياس قطري 33 بوصة بانسيابية عبر لوحة العدادات، لتجمع المعلومات والترفيه وعناصر التحكم في واجهة واحدة تتميز بوضوح استثنائي. وعند اقترانها بشاشة العرض الأمامية الملوّنة المتوفرة، تُعرض المعلومات الأساسية، مثل السرعة وإرشادات الملاحة، مباشرة على الزجاج الأمامي، لتبقي كل ما تحتاج إليه ضمن مجال رؤيتك، وتحافظ على تركيزك أثناء القيادة.',
          },
          type: 'interior',
        },
        '4w2u45bmimkf': {
          label: {
            en: '19-Speaker Akg Studio Audio System With Dolby Atmos',
            ar: 'نظام AKG Studio الصوتي مع Dolby Atmos و19 مكبراً للصوت',
          },
          image: '',
          title: {
            en: '19-Speaker Akg Studio Audio System With Dolby Atmos',
            ar: 'نظام AKG Studio الصوتي مع Dolby Atmos و19 مكبراً للصوت',
          },
          desc: {
            en: 'The standard AKG Studio Audio System with Dolby Atmos transforms every journey into an immersive listening experience. With 19 precisely tuned speakers, it delivers exceptional clarity, balance, and depth throughout the cabin. The system turns sound into craftsmanship – surrounding every passenger in pure, detailed audio that elevates every drive.',
            ar: 'يحوّل نظام AKG Studio الصوتي مع Dolby Atmos، والمزوّد بـ19 مكبراً للصوت كتجهيز قياسي، كل رحلة إلى تجربة استماع غامرة. وبفضل 19 مكبراً للصوت جرى ضبطها بدقة، يوفر النظام وضوحاً استثنائياً، وتوازناً مثالياً، وعمقاً غنياً في مختلف أنحاء المقصورة. ويحيط كل راكب بصوت نقي وغني بالتفاصيل، ليمنح تجربة صوتية استثنائية في كل رحلة.',
          },
          type: 'interior',
        },
        zdesiualecwi: {
          label: {
            en: 'Lean Proportions',
            ar: 'أبعاد انسيابية',
          },
          image: 'images/2366/Front_Side_View.jpg',
          title: {
            en: 'Lean Proportions',
            ar: 'أبعاد انسيابية',
          },
          desc: {
            en: 'OPTIQ’s lean proportions contribute to a nimble ride that complements the smooth, near-instant torque of its Ultium battery platform. Several innovative solutions, such as a vented rear spoiler, diffusers and other sculptural elements, allow aggressive car-like aerodynamic efficiency.',
            ar: 'تسهم أبعاد OPTIQ الانسيابية في منحها رشاقة على الطريق، تتكامل مع عزم الدوران السلس وشبه الفوري الذي توفره منصة بطارية Ultium. كما تعزز مجموعة من الحلول المبتكرة، مثل الجناح الخلفي المزود بفتحات تهوية وموزعات الهواء وغيرها من العناصر المنحوتة، كفاءتها الديناميكية الهوائية بطابع رياضي جريء.',
          },
          type: 'exterior',
        },
        wi021bet8rkn: {
          label: {
            en: 'Black Crystal Grille',
            ar: 'شبكة سوداء كريستالية',
          },
          image: 'images/2366/Front_Grill.jpg',
          title: {
            en: 'Black Crystal Grille',
            ar: 'شبكة سوداء كريستالية',
          },
          desc: {
            en: 'A black crystal grille establishes the face of OPTIQ, with an illuminated Cadillac crest and bold design elements. Refined linework draws the eye to unique visual elements complemented with sharp sculptural details in the grille as well as the tail and headlamps.',
            ar: 'ترسم الشبكة السوداء الكريستالية ملامح واجهة OPTIQ، وتتوسطها شارة كاديلاك المضيئة وعناصر تصميم جريئة. وتلفت الخطوط المتقنة الأنظار إلى تفاصيل بصرية فريدة، تكتمل بلمسات منحوتة وحادة في الشبكة والمصابيح الأمامية والخلفية.',
          },
          type: 'exterior',
        },
        jleviy76yfqe: {
          label: {
            en: 'Rear Quarter-Panel Detail',
            ar: 'تفاصيل اللوحة الجانبية الخلفية',
          },
          image: 'images/2366/Rear_Quarter_View.jpg',
          title: {
            en: 'Rear Quarter-Panel Detail',
            ar: 'تفاصيل اللوحة الجانبية الخلفية',
          },
          desc: {
            en: 'High-gloss black lower body components around the wheels and doors draw the viewer’s eye up and emphasize OPTIQ’s sleek design. A precision-patterned rear quarter-window graphic fades to the rear, strengthening the fastback effect of the low roofline.',
            ar: 'تلفت مكونات الهيكل السفلية باللون الأسود اللامع حول العجلات والأبواب الأنظار إلى الأعلى، وتبرز تصميم OPTIQ الانسيابي. كما يتلاشى نقش النافذة الجانبية الخلفية، المصمم بنمط دقيق، باتجاه الجزء الخلفي ليعزز طابع Fastback الذي يمنحه خط السقف المنخفض.',
          },
          type: 'exterior',
        },
        mn8oi8vwsr1l: {
          label: {
            en: 'Panoramic Glass Roof And Black Roof',
            ar: 'سقف بانورامي زجاجي وسقف أسود',
          },
          image: 'images/2366/Sunroof.jpg',
          title: {
            en: 'Panoramic Glass Roof And Black Roof',
            ar: 'سقف بانورامي زجاجي وسقف أسود',
          },
          desc: {
            en: 'The stunning panoramic glass roof enables a seamless transition from glass to sheet metal. The fixed full-glass roof stretches uninterrupted, welcoming natural light and providing an expansive view from every seat while maximizing headroom in the cabin. The available black roof provides exciting contrast, while a vented rear spoiler adds to appearance while allowing air to flow over the rear window, eliminating the need for a rear wiper.',
            ar: 'يحقق السقف البانورامي الزجاجي انتقالاً سلساً بين الزجاج وهيكل السيارة المعدني. ويمتد السقف الزجاجي الثابت بالكامل دون انقطاع، ليدخل الضوء الطبيعي ويوفر إطلالة واسعة من كل مقعد، مع تعزيز مساحة الرأس داخل المقصورة. ويمنح السقف الأسود المتاح تبايناً لافتاً، فيما يضفي الجناح الخلفي المزود بفتحات تهوية لمسة مميزة ويسمح بتدفق الهواء فوق النافذة الخلفية، ما يلغي الحاجة إلى ماسحة خلفية.',
          },
          type: 'exterior',
        },
        bzmmdartkj4j21: {
          label: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          image: 'images/2366/Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'العجلات',
          },
          desc: {
            en: '21" 6-Spoke Reverse Rim alloy wheels with Satin Graphite finish and Laser-Etched V-Series emblem',
            ar: 'عجلات ألمنيوم قياس 21 بوصة بتصميم Reverse Rim سداسي الأذرع، بلمسة Satin Graphite وشعار V-Series محفور بالليزر',
          },
          type: 'exterior',
        },
        '9mkaqqs8ersr': {
          label: {
            en: 'Innovative Materials',
            ar: 'مواد مبتكرة',
          },
          image: 'images/2366/Interior_view.jpg',
          title: {
            en: 'Innovative Materials',
            ar: 'مواد مبتكرة',
          },
          desc: {
            en: 'OPTIQ introduces new style with its available PaperWood décor. PaperWood veneer is made up of equal parts of tulip wood and recycled newspaper. This surface creates a harmonious balance between nature and modernity – avant-garde meets accessible. Fabrics provide a pleasant, tactile touch and striking appearance. OPTIQ’s patterned accent fabric is woven from yarn made from 100% recycled materials.',
            ar: 'تقدّم OPTIQ أسلوباً جديداً مع خامة PaperWood المتوفرة. وتتكوّن خامة PaperWood من أجزاء متساوية من خشب التوليب والصحف المعاد تدويرها، لتوفّر سطحاً يجمع بتناغم بين الطبيعة والحداثة، حيث يلتقي التصميم الطليعي بسهولة الاستخدام. كما توفّر الأقمشة ملمساً مريحاً ومظهراً لافتاً، فيما يُنسج قماش الزخارف المميز في OPTIQ من خيوط مصنوعة من مواد معاد تدويرها بنسبة 100%.',
          },
          type: 'interior',
        },
        s2ezeadsngz7: {
          label: {
            en: 'Dual-Zone Ambient Lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة',
          },
          image: 'images/2366/Ambient_Lighting.jpg',
          title: {
            en: 'Dual-Zone Ambient Lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة',
          },
          desc: {
            en: 'Inside OPTIQ, light sets the tone. The 126-color dual-zone ambient lighting lets you shape the cabin’s atmosphere to match every mood – calm, bold, or anything in between. Subtle illumination lines flow across the interior, enhancing its sculpted surfaces and sense of spaciousness.',
            ar: 'داخل OPTIQ، تبدأ التجربة مع الإضاءة. تتيح لك الإضاءة المحيطية ثنائية المناطق، المتوفرة بـ126 لوناً، تصميم أجواء المقصورة بما يناسب كل لحظة، سواء كنت تفضل أجواءً هادئة أو جريئة أو ما بينهما. وتمتد خطوط الإضاءة بانسيابية عبر المقصورة، لتبرز تفاصيل تصميمها وتعزز الإحساس بالرحابة.',
          },
          type: 'interior',
        },
        qasbrtm6gfyy47: {
          label: {
            en: 'Signature Seating',
            ar: 'مقاعد بتوقيع V-Series',
          },
          image: 'images/2366/Signature_Seating.jpg',
          title: {
            en: 'Signature Seating',
            ar: 'مقاعد بتوقيع V-Series',
          },
          desc: {
            en: 'Iconic V-Series signature driver seat embroidery is offered with Inteluxe seats with perforated inserts',
            ar: 'يتوفر التطريز المميز بتوقيع V-Series على مقعد السائق، مع مقاعد Inteluxe وحشوات مثقبة.',
          },
          type: 'interior',
        },
        qaopbrtm6gfyy47: {
          label: {
            en: 'Performance Steering Wheel',
            ar: 'عجلة قيادة للأداء',
          },
          image: 'images/2366/Steering.jpg',
          title: {
            en: 'Performance Steering Wheel',
            ar: 'عجلة قيادة للأداء',
          },
          desc: {
            en: 'Unique to our V-Series EV lineup for a sure grip and easy access to V-Mode, a Cadillac-exclusive customizable setting',
            ar: 'تصميم حصري لمجموعة سيارات V-Series الكهربائية، يمنحك قبضة محكمة ووصولاً سهلاً إلى V-Mode، إعداد كاديلاك الحصري القابل للتخصيص.',
          },
          type: 'interior',
        },
        h2fv1pe6q2dc: {
          label: {
            en: 'Segment-Best Second-Row Legroom',
            ar: 'مساحة الأرجل في الصف الثاني الأفضل في فئتها',
          },
          image: 'images/2366/Middle_Row_Legroom.jpg',
          title: {
            en: 'Segment-Best Second-Row Legroom',
            ar: 'مساحة الأرجل في الصف الثاني الأفضل في فئتها',
          },
          desc: {
            en: 'OPTIQ has segment-best rear legroom of 960mm making it a comfortable space to stretch out and enjoy the ride. Rear passengers also have two USB-C ports and vents for convenience.',
            ar: 'توفر OPTIQ أفضل مساحة للأرجل في المقاعد الخلفية ضمن فئتها، تبلغ 960 مم، لتمنح الركاب رحابة استثنائية وراحة أكبر في كل رحلة. كما زُوّد الركاب في المقاعد الخلفية بمنفذي USB-C وفتحات تهوية، لمزيد من الراحة.',
          },
          type: 'interior',
        },
        rglk1xge652a: {
          label: {
            en: '33-Inch Diagonal Advanced Led Display',
            ar: 'شاشة LED متطورة بقياس قطري 33 بوصة',
          },
          image: 'images/2366/Info_Screen.jpg',
          title: {
            en: '33-Inch Diagonal Advanced Led Display',
            ar: 'شاشة LED متطورة بقياس قطري 33 بوصة',
          },
          desc: {
            en: 'The 33” diagonal advanced LED display flows across the dashboard in a continuous curve, uniting information, entertainment, and control in vivid clarity. Paired with the available full-color Head-Up Display, key details such as speed and navigation are projected directly onto the windshield – creating a single, intuitive interface that keeps every command within sight and every drive in focus.',
            ar: 'تمتد شاشة LED المتطورة بقياس قطري 33 بوصة بانسيابية عبر لوحة العدادات، لتجمع المعلومات والترفيه وعناصر التحكم في واجهة واحدة تتميز بوضوح استثنائي. وعند اقترانها بشاشة العرض الأمامية الملوّنة المتوفرة، تُعرض المعلومات الأساسية، مثل السرعة وإرشادات الملاحة، مباشرة على الزجاج الأمامي، لتبقي كل ما تحتاج إليه ضمن مجال رؤيتك، وتحافظ على تركيزك أثناء القيادة.',
          },
          type: 'interior',
        },
        bmrgvh33m9hw: {
          label: {
            en: '19-Speaker Akg Studio Audio System With Dolby Atmos',
            ar: 'نظام AKG Studio الصوتي مع Dolby Atmos و19 مكبراً للصوت',
          },
          image: 'images/2366/Speakers.jpg',
          title: {
            en: '19-Speaker Akg Studio Audio System With Dolby Atmos',
            ar: 'نظام AKG Studio الصوتي مع Dolby Atmos و19 مكبراً للصوت',
          },
          desc: {
            en: 'The standard AKG Studio Audio System with Dolby Atmos transforms every journey into an immersive listening experience. With 19 precisely tuned speakers, it delivers exceptional clarity, balance, and depth throughout the cabin. The system turns sound into craftsmanship – surrounding every passenger in pure, detailed audio that elevates every drive.',
            ar: 'يحوّل نظام AKG Studio الصوتي مع Dolby Atmos، والمزوّد بـ19 مكبراً للصوت كتجهيز قياسي، كل رحلة إلى تجربة استماع غامرة. وبفضل 19 مكبراً للصوت جرى ضبطها بدقة، يوفر النظام وضوحاً استثنائياً، وتوازناً مثالياً، وعمقاً غنياً في مختلف أنحاء المقصورة. ويحيط كل راكب بصوت نقي وغني بالتفاصيل، ليمنح تجربة صوتية استثنائية في كل رحلة.',
          },
          type: 'interior',
        },
        qxg0y3opafre: {
          label: {
            en: 'Choreographed Exterior Led Lighting',
            ar: 'أضواء خارجية LED راقصة',
          },
          image: 'images/2366/optiq_pre_lux_2026/Exterior_Anim_Lights.jpg',
          title: {
            en: 'Choreographed Exterior Led Lighting',
            ar: 'أضواء خارجية LED راقصة',
          },
          desc: {
            en: 'Available lighting choreography greets the owner upon arrival with an animated light sequence that flows from the grille, headlamps and door handles before finishing on the rear taillamps.',
            ar: 'تستقبل الإضاءة الراقصة المتاحة المالك عند اقترابه بتسلسل ضوئي متحرك يبدأ من الشبكة، ويمتد إلى المصابيح الأمامية ومقابض الأبواب، قبل أن يختتم عند المصابيح الخلفية.',
          },
          type: 'exterior',
        },
        q78piidva3ud: {
          label: {
            en: 'Lean Proportions',
            ar: 'أبعاد انسيابية',
          },
          image: 'images/2366/optiq_pre_lux_2026/Front_Side_View.jpg',
          title: {
            en: 'Lean Proportions',
            ar: 'أبعاد انسيابية',
          },
          desc: {
            en: 'OPTIQ’s lean proportions contribute to a nimble ride that complements the smooth, near-instant torque of its Ultium battery platform. Several innovative solutions, such as a vented rear spoiler, diffusers and other sculptural elements, allow aggressive car-like aerodynamic efficiency.',
            ar: 'تسهم أبعاد OPTIQ الانسيابية في منحها رشاقة على الطريق، تتكامل مع عزم الدوران السلس وشبه الفوري الذي توفره منصة بطارية Ultium. كما تعزز مجموعة من الحلول المبتكرة، مثل الجناح الخلفي المزود بفتحات تهوية وموزعات الهواء وغيرها من العناصر المنحوتة، كفاءتها الديناميكية الهوائية بطابع رياضي جريء.',
          },
          type: 'exterior',
        },
        p852y9tfv7t9: {
          label: {
            en: 'Black Crystal Grille',
            ar: 'شبكة سوداء كريستالية',
          },
          image: 'images/2366/optiq_pre_lux_2026/Front_Grill.jpg',
          title: {
            en: 'Black Crystal Grille',
            ar: 'شبكة سوداء كريستالية',
          },
          desc: {
            en: 'A black crystal grille establishes the face of OPTIQ, with an illuminated Cadillac crest and bold design elements. Refined linework draws the eye to unique visual elements complemented with sharp sculptural details in the grille as well as the tail and headlamps.',
            ar: 'ترسم الشبكة السوداء الكريستالية ملامح واجهة OPTIQ، وتتوسطها شارة كاديلاك المضيئة وعناصر تصميم جريئة. وتلفت الخطوط المتقنة الأنظار إلى تفاصيل بصرية فريدة، تكتمل بلمسات منحوتة وحادة في الشبكة والمصابيح الأمامية والخلفية.',
          },
          type: 'exterior',
        },
        '3ofh8yzwbhdc': {
          label: {
            en: 'Rear Quarter-Panel Detail',
            ar: 'تفاصيل اللوحة الجانبية الخلفية',
          },
          image: 'images/2366/optiq_pre_lux_2026/Rear_Quarter_View.jpg',
          title: {
            en: 'Rear Quarter-Panel Detail',
            ar: 'تفاصيل اللوحة الجانبية الخلفية',
          },
          desc: {
            en: 'High-gloss black lower body components around the wheels and doors draw the viewer’s eye up and emphasize OPTIQ’s sleek design. A precision-patterned rear quarter-window graphic fades to the rear, strengthening the fastback effect of the low roofline.',
            ar: 'تلفت مكونات الهيكل السفلية باللون الأسود اللامع حول العجلات والأبواب الأنظار إلى الأعلى، وتبرز تصميم OPTIQ الانسيابي. كما يتلاشى نقش النافذة الجانبية الخلفية، المصمم بنمط دقيق، باتجاه الجزء الخلفي ليعزز طابع Fastback الذي يمنحه خط السقف المنخفض.',
          },
          type: 'exterior',
        },
        eogysgpcrofp: {
          label: {
            en: 'Panoramic Glass Roof And Black Roof',
            ar: 'سقف بانورامي زجاجي وسقف أسود',
          },
          image: 'images/2366/optiq_pre_lux_2026/Sunroof.jpg',
          title: {
            en: 'Panoramic Glass Roof And Black Roof',
            ar: 'سقف بانورامي زجاجي وسقف أسود',
          },
          desc: {
            en: 'The stunning panoramic glass roof enables a seamless transition from glass to sheet metal. The fixed full-glass roof stretches uninterrupted, welcoming natural light and providing an expansive view from every seat while maximizing headroom in the cabin. The available black roof provides exciting contrast, while a vented rear spoiler adds to appearance while allowing air to flow over the rear window, eliminating the need for a rear wiper.',
            ar: 'يحقق السقف البانورامي الزجاجي انتقالاً سلساً بين الزجاج وهيكل السيارة المعدني. ويمتد السقف الزجاجي الثابت بالكامل دون انقطاع، ليدخل الضوء الطبيعي ويوفر إطلالة واسعة من كل مقعد، مع تعزيز مساحة الرأس داخل المقصورة. ويمنح السقف الأسود المتاح تبايناً لافتاً، فيما يضفي الجناح الخلفي المزود بفتحات تهوية لمسة مميزة ويسمح بتدفق الهواء فوق النافذة الخلفية، ما يلغي الحاجة إلى ماسحة خلفية.',
          },
          type: 'exterior',
        },
        sfk75k7d0m4s: {
          label: {
            en: 'Dual-Zone Ambient Lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة',
          },
          image: 'images/2366/optiq_pre_lux_2026/Ambient_Lighting.jpg',
          title: {
            en: 'Dual-Zone Ambient Lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة',
          },
          desc: {
            en: 'Inside OPTIQ, light sets the tone. The 126-color dual-zone ambient lighting lets you shape the cabin’s atmosphere to match every mood – calm, bold, or anything in between. Subtle illumination lines flow across the interior, enhancing its sculpted surfaces and sense of spaciousness.',
            ar: 'داخل OPTIQ، تبدأ التجربة مع الإضاءة. تتيح لك الإضاءة المحيطية ثنائية المناطق، المتوفرة بـ126 لوناً، تصميم أجواء المقصورة بما يناسب كل لحظة، سواء كنت تفضل أجواءً هادئة أو جريئة أو ما بينهما. وتمتد خطوط الإضاءة بانسيابية عبر المقصورة، لتبرز تفاصيل تصميمها وتعزز الإحساس بالرحابة.',
          },
          type: 'interior',
        },
        zz2trf9exnm0: {
          label: {
            en: 'Segment-Best Second-Row Legroom',
            ar: 'مساحة الأرجل في الصف الثاني الأفضل في فئتها',
          },
          image: 'images/2366/optiq_pre_lux_2026/Legroom.jpg',
          title: {
            en: 'Segment-Best Second-Row Legroom',
            ar: 'مساحة الأرجل في الصف الثاني الأفضل في فئتها',
          },
          desc: {
            en: 'OPTIQ has segment-best rear legroom of 960mm making it a comfortable space to stretch out and enjoy the ride. Rear passengers also have two USB-C ports and vents for convenience.',
            ar: 'توفر OPTIQ أفضل مساحة للأرجل في المقاعد الخلفية ضمن فئتها، تبلغ 960 مم، لتمنح الركاب رحابة استثنائية وراحة أكبر في كل رحلة. كما زُوّد الركاب في المقاعد الخلفية بمنفذي USB-C وفتحات تهوية، لمزيد من الراحة.',
          },
          type: 'interior',
        },
        u6tufvfiuhhz: {
          label: {
            en: '33-Inch Diagonal Advanced Led Display',
            ar: 'شاشة LED متطورة بقياس قطري 33 بوصة',
          },
          image: 'images/2366/optiq_pre_lux_2026/Info_Screen.jpg',
          title: {
            en: '33-Inch Diagonal Advanced Led Display',
            ar: 'شاشة LED متطورة بقياس قطري 33 بوصة',
          },
          desc: {
            en: 'The 33” diagonal advanced LED display flows across the dashboard in a continuous curve, uniting information, entertainment, and control in vivid clarity. Paired with the available full-color Head-Up Display, key details such as speed and navigation are projected directly onto the windshield – creating a single, intuitive interface that keeps every command within sight and every drive in focus.',
            ar: 'تمتد شاشة LED المتطورة بقياس قطري 33 بوصة بانسيابية عبر لوحة العدادات، لتجمع المعلومات والترفيه وعناصر التحكم في واجهة واحدة تتميز بوضوح استثنائي. وعند اقترانها بشاشة العرض الأمامية الملوّنة المتوفرة، تُعرض المعلومات الأساسية، مثل السرعة وإرشادات الملاحة، مباشرة على الزجاج الأمامي، لتبقي كل ما تحتاج إليه ضمن مجال رؤيتك، وتحافظ على تركيزك أثناء القيادة.',
          },
          type: 'interior',
        },
        v4i019a0k6qk: {
          label: {
            en: '19-Speaker Akg Studio Audio System With Dolby Atmos',
            ar: 'نظام AKG Studio الصوتي مع Dolby Atmos و19 مكبراً للصوت',
          },
          image: 'images/2366/optiq_pre_lux_2026/Speakers.jpg',
          title: {
            en: '19-Speaker Akg Studio Audio System With Dolby Atmos',
            ar: 'نظام AKG Studio الصوتي مع Dolby Atmos و19 مكبراً للصوت',
          },
          desc: {
            en: 'The standard AKG Studio Audio System with Dolby Atmos transforms every journey into an immersive listening experience. With 19 precisely tuned speakers, it delivers exceptional clarity, balance, and depth throughout the cabin. The system turns sound into craftsmanship – surrounding every passenger in pure, detailed audio that elevates every drive.',
            ar: 'يحوّل نظام AKG Studio الصوتي مع Dolby Atmos، والمزوّد بـ19 مكبراً للصوت كتجهيز قياسي، كل رحلة إلى تجربة استماع غامرة. وبفضل 19 مكبراً للصوت جرى ضبطها بدقة، يوفر النظام وضوحاً استثنائياً، وتوازناً مثالياً، وعمقاً غنياً في مختلف أنحاء المقصورة. ويحيط كل راكب بصوت نقي وغني بالتفاصيل، ليمنح تجربة صوتية استثنائية في كل رحلة.',
          },
          type: 'interior',
        },
        '29kx4ja7eg7r': {
          label: {
            en: 'Choreographed Exterior Led Lighting',
            ar: 'أضواء خارجية LED راقصة',
          },
          image: 'images/2366/opt_lux_2026/Exterior_Anim_Lights.jpg',
          title: {
            en: 'Choreographed Exterior Led Lighting',
            ar: 'أضواء خارجية LED راقصة',
          },
          desc: {
            en: 'Available lighting choreography greets the owner upon arrival with an animated light sequence that flows from the grille, headlamps and door handles before finishing on the rear taillamps.',
            ar: 'تستقبل الإضاءة الراقصة المتاحة المالك عند اقترابه بتسلسل ضوئي متحرك يبدأ من الشبكة، ويمتد إلى المصابيح الأمامية ومقابض الأبواب، قبل أن يختتم عند المصابيح الخلفية.',
          },
          type: 'exterior',
        },
        '3ud49gqncb2e': {
          label: {
            en: 'Lean Proportions',
            ar: 'أبعاد انسيابية',
          },
          image: 'images/2366/opt_lux_2026/Front_Side_View.jpg',
          title: {
            en: 'Lean Proportions',
            ar: 'أبعاد انسيابية',
          },
          desc: {
            en: 'OPTIQ’s lean proportions contribute to a nimble ride that complements the smooth, near-instant torque of its Ultium battery platform. Several innovative solutions, such as a vented rear spoiler, diffusers and other sculptural elements, allow aggressive car-like aerodynamic efficiency.',
            ar: 'تسهم أبعاد OPTIQ الانسيابية في منحها رشاقة على الطريق، تتكامل مع عزم الدوران السلس وشبه الفوري الذي توفره منصة بطارية Ultium. كما تعزز مجموعة من الحلول المبتكرة، مثل الجناح الخلفي المزود بفتحات تهوية وموزعات الهواء وغيرها من العناصر المنحوتة، كفاءتها الديناميكية الهوائية بطابع رياضي جريء.',
          },
          type: 'exterior',
        },
        qcwd61vh2s9y: {
          label: {
            en: 'Black Crystal Grille',
            ar: 'شبكة سوداء كريستالية',
          },
          image: 'images/2366/opt_lux_2026/Front_Grill.jpg',
          title: {
            en: 'Black Crystal Grille',
            ar: 'شبكة سوداء كريستالية',
          },
          desc: {
            en: 'A black crystal grille establishes the face of OPTIQ, with an illuminated Cadillac crest and bold design elements. Refined linework draws the eye to unique visual elements complemented with sharp sculptural details in the grille as well as the tail and headlamps.',
            ar: 'ترسم الشبكة السوداء الكريستالية ملامح واجهة OPTIQ، وتتوسطها شارة كاديلاك المضيئة وعناصر تصميم جريئة. وتلفت الخطوط المتقنة الأنظار إلى تفاصيل بصرية فريدة، تكتمل بلمسات منحوتة وحادة في الشبكة والمصابيح الأمامية والخلفية.',
          },
          type: 'exterior',
        },
        pcwqd3petb29: {
          label: {
            en: 'Rear Quarter-Panel Detail',
            ar: 'تفاصيل اللوحة الجانبية الخلفية',
          },
          image: 'images/2366/opt_lux_2026/Rear_Quarter_View.jpg',
          title: {
            en: 'Rear Quarter-Panel Detail',
            ar: 'تفاصيل اللوحة الجانبية الخلفية',
          },
          desc: {
            en: 'High-gloss black lower body components around the wheels and doors draw the viewer’s eye up and emphasize OPTIQ’s sleek design. A precision-patterned rear quarter-window graphic fades to the rear, strengthening the fastback effect of the low roofline.',
            ar: 'تلفت مكونات الهيكل السفلية باللون الأسود اللامع حول العجلات والأبواب الأنظار إلى الأعلى، وتبرز تصميم OPTIQ الانسيابي. كما يتلاشى نقش النافذة الجانبية الخلفية، المصمم بنمط دقيق، باتجاه الجزء الخلفي ليعزز طابع Fastback الذي يمنحه خط السقف المنخفض.',
          },
          type: 'exterior',
        },
        bzmmdakj4j21: {
          label: {
            en: 'Panoramic Glass Roof And Black Roof',
            ar: 'سقف بانورامي زجاجي وسقف أسود',
          },
          image: 'images/2366/opt_lux_2026/Sunroof.jpg',
          title: {
            en: 'Panoramic Glass Roof And Black Roof',
            ar: 'سقف بانورامي زجاجي وسقف أسود',
          },
          desc: {
            en: 'The stunning panoramic glass roof enables a seamless transition from glass to sheet metal. The fixed full-glass roof stretches uninterrupted, welcoming natural light and providing an expansive view from every seat while maximizing headroom in the cabin. The available black roof provides exciting contrast, while a vented rear spoiler adds to appearance while allowing air to flow over the rear window, eliminating the need for a rear wiper.',
            ar: 'يحقق السقف البانورامي الزجاجي انتقالاً سلساً بين الزجاج وهيكل السيارة المعدني. ويمتد السقف الزجاجي الثابت بالكامل دون انقطاع، ليدخل الضوء الطبيعي ويوفر إطلالة واسعة من كل مقعد، مع تعزيز مساحة الرأس داخل المقصورة. ويمنح السقف الأسود المتاح تبايناً لافتاً، فيما يضفي الجناح الخلفي المزود بفتحات تهوية لمسة مميزة ويسمح بتدفق الهواء فوق النافذة الخلفية، ما يلغي الحاجة إلى ماسحة خلفية.',
          },
          type: 'exterior',
        },
        qasbm6gfyy47: {
          label: {
            en: 'Dual-Zone Ambient Lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة',
          },
          image: 'images/2366/opt_lux_2026/Ambient_Lighting.jpg',
          title: {
            en: 'Dual-Zone Ambient Lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة',
          },
          desc: {
            en: 'Inside OPTIQ, light sets the tone. The 126-color dual-zone ambient lighting lets you shape the cabin’s atmosphere to match every mood – calm, bold, or anything in between. Subtle illumination lines flow across the interior, enhancing its sculpted surfaces and sense of spaciousness.',
            ar: 'داخل OPTIQ، تبدأ التجربة مع الإضاءة. تتيح لك الإضاءة المحيطية ثنائية المناطق، المتوفرة بـ126 لوناً، تصميم أجواء المقصورة بما يناسب كل لحظة، سواء كنت تفضل أجواءً هادئة أو جريئة أو ما بينهما. وتمتد خطوط الإضاءة بانسيابية عبر المقصورة، لتبرز تفاصيل تصميمها وتعزز الإحساس بالرحابة.',
          },
          type: 'interior',
        },
        gip7cgbu25uv: {
          label: {
            en: 'Segment-Best Second-Row Legroom',
            ar: 'مساحة الأرجل في الصف الثاني الأفضل في فئتها',
          },
          image: 'images/2366/opt_lux_2026/Middle_Row_Legroom.jpg',
          title: {
            en: 'Segment-Best Second-Row Legroom',
            ar: 'مساحة الأرجل في الصف الثاني الأفضل في فئتها',
          },
          desc: {
            en: 'OPTIQ has segment-best rear legroom of 960mm making it a comfortable space to stretch out and enjoy the ride. Rear passengers also have two USB-C ports and vents for convenience.',
            ar: 'توفر OPTIQ أفضل مساحة للأرجل في المقاعد الخلفية ضمن فئتها، تبلغ 960 مم، لتمنح الركاب رحابة استثنائية وراحة أكبر في كل رحلة. كما زُوّد الركاب في المقاعد الخلفية بمنفذي USB-C وفتحات تهوية، لمزيد من الراحة.',
          },
          type: 'interior',
        },
        '77i0h4ejxjbt': {
          label: {
            en: '33-Inch Diagonal Advanced Led Display',
            ar: 'شاشة LED متطورة بقياس قطري 33 بوصة',
          },
          image: 'images/2366/opt_lux_2026/Info_Screen.jpg',
          title: {
            en: '33-Inch Diagonal Advanced Led Display',
            ar: 'شاشة LED متطورة بقياس قطري 33 بوصة',
          },
          desc: {
            en: 'The 33” diagonal advanced LED display flows across the dashboard in a continuous curve, uniting information, entertainment, and control in vivid clarity. Paired with the available full-color Head-Up Display, key details such as speed and navigation are projected directly onto the windshield – creating a single, intuitive interface that keeps every command within sight and every drive in focus.',
            ar: 'تمتد شاشة LED المتطورة بقياس قطري 33 بوصة بانسيابية عبر لوحة العدادات، لتجمع المعلومات والترفيه وعناصر التحكم في واجهة واحدة تتميز بوضوح استثنائي. وعند اقترانها بشاشة العرض الأمامية الملوّنة المتوفرة، تُعرض المعلومات الأساسية، مثل السرعة وإرشادات الملاحة، مباشرة على الزجاج الأمامي، لتبقي كل ما تحتاج إليه ضمن مجال رؤيتك، وتحافظ على تركيزك أثناء القيادة.',
          },
          type: 'interior',
        },
        '1jhh1m7485kl': {
          label: {
            en: '19-Speaker Akg Studio Audio System With Dolby Atmos',
            ar: 'نظام AKG Studio الصوتي مع Dolby Atmos و19 مكبراً للصوت',
          },
          image: 'images/2366/opt_lux_2026/Speakers.jpg',
          title: {
            en: '19-Speaker Akg Studio Audio System With Dolby Atmos',
            ar: 'نظام AKG Studio الصوتي مع Dolby Atmos و19 مكبراً للصوت',
          },
          desc: {
            en: 'The standard AKG Studio Audio System with Dolby Atmos transforms every journey into an immersive listening experience. With 19 precisely tuned speakers, it delivers exceptional clarity, balance, and depth throughout the cabin. The system turns sound into craftsmanship – surrounding every passenger in pure, detailed audio that elevates every drive.',
            ar: 'يحوّل نظام AKG Studio الصوتي مع Dolby Atmos، والمزوّد بـ19 مكبراً للصوت كتجهيز قياسي، كل رحلة إلى تجربة استماع غامرة. وبفضل 19 مكبراً للصوت جرى ضبطها بدقة، يوفر النظام وضوحاً استثنائياً، وتوازناً مثالياً، وعمقاً غنياً في مختلف أنحاء المقصورة. ويحيط كل راكب بصوت نقي وغني بالتفاصيل، ليمنح تجربة صوتية استثنائية في كل رحلة.',
          },
          type: 'interior',
        },
      },
      accessories: {
        '3ckvx6gvve67': {
          label: {
            en: 'Front Badge Illuminated',
            ar: 'الشعار الأمامي مضيء',
          },
          image: 'images/2366/front_badge_illuminated.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC01',
        },
        '60cyi4386dh7': {
          label: {
            en: 'Side Mirror Cap',
            ar: 'أغطية المرايا',
          },
          image: 'images/2366/orvm_cap.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '5JR',
          brandItemCode: 'AC02',
        },
        '26awwpydj3k4': {
          label: {
            en: 'Roof CrossRails',
            ar: 'قضبان سقفية متقاطعة',
          },
          image: 'images/2366/roof_crossrails.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC207',
        },
        '41j05csmotu4': {
          label: {
            en: 'Side Mud Guards',
            ar: 'واقيات الرذاذ الجانبية',
          },
          image: 'images/2366/side_mud_gaurd.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VRV',
          brandItemCode: 'AC04',
        },
        '4g3h4ls28grj': {
          label: {
            en: 'Side Perimeter Lamps',
            ar: 'مصابيح جانبية',
          },
          image: 'images/2366/side_perimeter_lamps.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC05',
        },
        '56cy0d0cnz7u': {
          label: {
            en: 'Floor Mat',
            ar: 'سجاد الأرضية',
          },
          image: 'images/2366/floor_mat.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC06',
        },
        '2esb5gdmfpk8': {
          label: {
            en: 'Floor Liner',
            ar: 'بطانة الأرضية',
          },
          image: 'images/2366/floor_liner.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC07',
        },
        '496aqzregvz5': {
          label: {
            en: 'Trunk Cargo BoxMat',
            ar: 'سجاد صندوق الأمتعة',
          },
          image: 'images/2366/trunk_cargo_boxmat.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'W0A',
          brandItemCode: 'AC08',
        },
        '4e8rsvm8yulq': {
          label: {
            en: 'Trunk Cargo Net',
            ar: 'شبك صندوق الأمتعة',
          },
          image: 'images/2366/trunk_cargo_net.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'W2D',
          brandItemCode: 'AC09',
        },
        '6bmwunaxygjc': {
          label: {
            en: 'Trunk Cargo Organiser',
            ar: 'منظم صندوق الأمتعة',
          },
          image: 'images/2366/trunk_cargo_organiser.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC10',
        },
        '3l2bs2aiit7y': {
          label: {
            en: 'Trunk Cargo Shade VRS',
            ar: 'ستارة شمسية لصندوق الأمتعة',
          },
          image: 'images/2366/trunk_cargo_shade.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'VRS',
          brandItemCode: 'AC230',
        },
        '50t7txtk3kvs': {
          label: {
            en: 'Trunk Cargo Tray CAV',
            ar: 'أرضية صندوق الأمتعة',
          },
          image: 'images/2366/trunk_cargo_tray.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CAV',
          brandItemCode: 'AC231',
        },
        '3ktjpvxvvvkj': {
          label: {
            en: 'Trunk Cargo Tray SHX',
            ar: 'أرضية صندوق الأمتعة',
          },
          image: 'images/2366/trunk_cargo_tray_shx.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC232',
        },
        z7ne7fg1ds4c: {
          label: {
            en: 'Variants parts_VSERIES',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        g3ieipszphfu: {
          label: {
            en: 'WO_Santorini_Blue_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        a9hj0iqo1mct: {
          label: {
            en: 'W_Santorini_Blue_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        aanz6fiqy06r: {
          label: {
            en: 'WO_Carbon_Fiber_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        cii7k7g4ittk: {
          label: {
            en: 'W_Carbon_Fiber_Package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '5ckoklookvx6gpve78': {
          label: {
            en: 'Carpet_V_Series_Logo',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '5cklopookvx6gpve78': {
          label: {
            en: 'Black_Roof',
            ar: '',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'سَطح',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '41T',
          brandItemCode: 'OP02',
        },
        '5ckmnjxlo6gpve78': {
          label: {
            en: 'Alloy_Calipers_Default_VSeries',
            ar: '',
          },
          image: '',
          category: {
            en: 'Caliper',
            ar: 'ملاقط المكابح',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '5ckmrrxlo6gpve78': {
          label: {
            en: 'Calipers, Black, with Cadillac script',
            ar: 'ملاقط مكابح باللون الأسود تحمل شعار كاديلاك',
          },
          image: 'images/2366/vseries_acc_CALIPER_OPTIONAL.jpg',
          category: {
            en: 'Caliper',
            ar: 'ملاقط المكابح',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'J6A',
          brandItemCode: 'AC160',
        },
        '5cksewrxlo6gpve78': {
          label: {
            en: 'Carpet_Default',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '5cksepolo6gpve78': {
          label: {
            en: 'Floor mats, deluxe carpeted',
            ar: 'دواسات أرضية فاخرة مكسوة بالسجاد',
          },
          image: 'images/2366/vseries_acc_mat.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC181',
        },
        '2ud18g1zcqja': {
          label: {
            en: 'Variants parts_Pre_Lux',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '4r9ujh1ms88e': {
          label: {
            en: 'Carpet_Default',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '5ckokvx6gvve78': {
          label: {
            en: 'Floor mats, deluxe carpeted',
            ar: 'دواسات أرضية فاخرة مكسوة بالسجاد',
          },
          image: 'images/2366/prem_lux_CARPET_OPTIONAL.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC181',
        },
        bdbqb1zg1dr5: {
          label: {
            en: 'Colorbody_Roof',
            ar: '',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'سَطح',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ti5n7zx2wp04: {
          label: {
            en: 'Black Roof',
            ar: 'سقف أسود',
          },
          image: 'images/2366/prem_lux_ROOF_OPTIONAL.jpg',
          category: {
            en: 'Roof',
            ar: 'سَطح',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '41T',
          brandItemCode: 'OP02',
        },
        '3ckvx6gvve78': {
          label: {
            en: 'Variants parts_Lux',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '3ckokvx6gvve78': {
          label: {
            en: 'Carpet_Default',
            ar: '',
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'اكسسوارات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '5ckokokvx6gvve78': {
          label: {
            en: 'Colorbody_Roof',
            ar: '',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'سَطح',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '5ckokokvx6gpve78': {
          label: {
            en: 'Black Roof',
            ar: 'سقف أسود',
          },
          image: 'images/2366/lux_acc_ROOF_OPTIONAL.jpg',
          category: {
            en: 'Roof',
            ar: 'سَطح',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: '41T',
          brandItemCode: 'OP02',
        },
      },
      packages: {
        x7vtyriufcgr0: {
          label: {
            en: 'Santorini Blue Package',
            ar: 'باقة لمسات باللون الأزرق Santorini',
          },
          image: 'images/2366/Blue_Seat_Belts.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'Santorini Blue Interior Package includes Santorini blue seat belts and front seat backs',
            ar: 'تتضمن باقة لمسات باللون الأزرق Santorini أحزمة أمان باللون الأزرق Santorini، وظهرَي المقعدين الأماميين باللون الأزرق Santorini.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Santorini blue seat belts',
                ar: 'أحزمة أمان باللون الأزرق Santorini',
              },
              accessoryDescription: {
                en: 'Santorini blue seat belts',
                ar: 'أحزمة أمان باللون الأزرق Santorini',
              },
              image: 'images/2366/Blue_Seat_Belts.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Santorini blue front seat backs',
                ar: 'ظهرَا المقعدين الأماميين باللون الأزرق Santorini',
              },
              accessoryDescription: {
                en: 'Santorini blue front seat backs',
                ar: 'ظهرَا المقعدين الأماميين باللون الأزرق Santorini',
              },
              image: 'images/2366/Front_Seats.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'A7I',
          brandItemCode: 'PA31',
        },
        x7vtyroklpiufcgr0: {
          label: {
            en: 'Carbon Fiber Package',
            ar: 'باقة ألياف الكربون',
          },
          image: 'images/2366/Colorbody_Spoiler.jpg',
          category: {
            en: 'Packages',
            ar: 'الباقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'Carbon Fiber Package includes genuine carbon fiber front splitter, body-color rear spoiler and rear diffuser.',
            ar: 'تتضمن باقة ألياف الكربون موزعاً أمامياً من ألياف الكربون الأصلية، وجناحاً خلفياً بلون الهيكل، وموزع هواء خلفياً.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Carbon fiber front splitter',
                ar: 'موزع أمامي من ألياف الكربون',
              },
              accessoryDescription: {
                en: 'Carbon fiber front splitter',
                ar: 'موزع أمامي من ألياف الكربون',
              },
              image: 'images/2366/Front_Splitter.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Body-color rear spoiler',
                ar: 'جناح خلفي بلون الهيكل',
              },
              accessoryDescription: {
                en: 'Body-color rear spoiler',
                ar: 'جناح خلفي بلون الهيكل',
              },
              image: 'images/2366/Colorbody_Spoiler.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Rear diffuser',
                ar: 'موزع هواء خلفي',
              },
              accessoryDescription: {
                en: 'Rear diffuser',
                ar: 'موزع هواء خلفي',
              },
              image: 'images/2366/Rear_Diffuser.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'CFZ',
          brandItemCode: 'PA27',
        },
      },
      accessoryCategories: {
        exterior: ['Caliper'],
        options: ['Options & Accessories'],
      },
      interactions: {
        xl0huhq8cgps: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        hdmtouug3mrz: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        '2mbv5mgd2j0t': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        dp3auttujwro: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        togk4c21fw9i: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        eylu0s3u2euf: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        c2wy0i8hrovk: {
          label: {
            en: 'Sunroof Shade',
            ar: 'ستارة السقف',
          },
          type: 'interaction',
        },
        '2r1ytw5hxg6p': {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '6dvsse8x5znz': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        vt6cdmgxpezd: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        nnc9ej7gplbx: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        '4fgs1ckkh5ji': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        j9ejmhggh9r0: {
          label: {
            en: 'Sunroof Shade',
            ar: 'ستارة السقف',
          },
          type: 'interaction',
        },
        '5fw1njuowv83': {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        qw15zz0wqp7s: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        atbbyogclf16: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        s3k5pi374ml2: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        '3hmjpixzot2a': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        onp9lnl4hjta: {
          label: {
            en: 'Sunroof Shade',
            ar: 'ستارة السقف',
          },
          type: 'interaction',
        },
        f9s7b4hdeiz7: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '1nad76ictp6j': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        '3cqy8q29b96c': {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        '2ca59s6yd3u9': {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        '1dzdmvhl8vp3': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        mAlX4SRB_per: {
          label: {
            en: 'Sunroof Shade',
            ar: 'ستارة السقف',
          },
          type: 'interaction',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج',
          },
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقعد الصف الأول',
          },
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقعد الصف الثاني',
          },
        },
      },
      name: {
        en: 'Cadillac Optiq',
        ar: 'كاديلاك أوبتيك',
      },
      specifications: {
        heading: {
          en: 'Cadillac Optiq',
          ar: 'كاديلاك أوبتيك',
        },
        trim: {
          en: 'OPTIQ-V',
          ar: 'OPTIQ-V',
        },
        price: {
          en: 'Starting from 235000 MSRP',
          ar: 'ابتداء من 235000 MSRP',
        },
        model: 'Cadillac Optiq',
        seatingCapacity: '5 Seats',
        dimensions: {
          length: '4821 mm',
          width: '',
          height: '',
          wheelbase: '2947 mm',
          groundClearance: '',
        },
        performance: [
          {
            value: '315 HP',
            unit: '',
            label: {
              en: 'Horsepower',
              ar: 'حصان',
            },
          },
          {
            value: '450 NM',
            unit: '',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران',
            },
          },
          {
            value: '511 KM',
            unit: '',
            label: {
              en: 'Range',
              ar: 'المدى',
            },
          },
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز',
            },
            subtitle: {
              en: 'CADILLAC OPTIQ',
              ar: 'كاديلاك OPTIQ',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية',
            },
            subtitle: {
              en: 'Black Raven, Vibrant White Tricoat, Coastal Blue Metallic, Celestial Metallic, Radiant Red Tintcoat, Argent Silver Metallic',
              ar: 'أسود Raven، أبيض Vibrant بثلاث طبقات، أزرق Coastal معدني، Celestial معدني، أحمر Radiant بطبقتين، فضي Argent معدني',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Interior',
              ar: 'التصميم الداخلي',
            },
            subtitle: {
              en: 'Noir, Sky Cool Gray with Santorini Blue accents, Autumn Canyon, Phantom Blue',
              ar: 'Noir، رمادي Sky Cool مع لمسات Santorini Blue، Autumn Canyon، أزرق Phantom',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه',
            },
            subtitle: {
              en: 'Segment-largest 33” diagonal advanced LED display with 9K resolution',
              ar: 'أكبر شاشة LED متطورة في فئتها بقياس قطري 33 بوصة وبدقة 9K',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات',
            },
            subtitle: {
              en: '2947 mm',
              ar: '2947 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Total Length',
              ar: 'الطول الإجمالي',
            },
            subtitle: {
              en: '4821 mm',
              ar: '4821 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Cargo Volume (Behind 2nd Row Seat)',
              ar: 'سعة الأمتعة خلف مقاعد الصف الثاني',
            },
            subtitle: {
              en: '736 litres',
              ar: '736 لتر',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Powertrain',
              ar: 'نظام الدفع',
            },
            subtitle: {
              en: 'Rear Wheel Drive & All Wheel Drive',
              ar: 'دفع خلفي ودفع رباعي',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar Basics',
              ar: 'باقة OnStar الأساسية',
            },
            subtitle: {
              en: '8 Years',
              ar: '8 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar One',
              ar: 'باقة OnStar One',
            },
            subtitle: {
              en: 'Upto 3 Years',
              ar: 'حتى 3 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'سعة المقاعد',
            },
            subtitle: {
              en: '5 Seats',
              ar: '5 مقاعد',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
        ],
      },
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: {
        en: 'Select Body Style',
        ar: 'اختر نمط الهيكل',
      },
      availableTrims: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      driveType: {
        en: 'Drive Type',
        ar: 'نوع القيادة',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: {
        en: 'Loading',
        ar: 'جارٍ التحميل',
      },
      variantLoading: {
        en: 'Trim is loading',
        ar: 'نموذج جارٍ التحميل',
      },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: {
        en: 'Go Back',
        ar: 'العودة',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'عرض مميز',
      },
      configurator: {
        en: 'Configurator',
        ar: 'المُكوِّن',
      },
      buildYourRide: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
      saveYourBuild: {
        en: 'Save Your Build',
        ar: 'احفظ تكوينك',
      },
      startingPrice: {
        en: 'Starting Price',
        ar: 'السعر الابتدائي',
      },
      msrp: {
        en: 'MSRP',
        ar: 'سعر التجزئة المقترح',
      },
      proceed: {
        en: 'Proceed',
        ar: 'متابعة',
      },
    },
    tabs: {
      config: {
        en: 'Config',
        ar: 'الإعداد',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلي',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: {
        en: 'Remove',
        ar: 'إزالة',
      },
      packageCategory: {
        en: 'Package',
        ar: 'حزمة',
      },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: {
        en: 'Save to WhatsApp',
        ar: 'حفظ على واتساب',
      },
      optionEmail: {
        en: 'Save to Email',
        ar: 'حفظ على البريد الإلكتروني',
      },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyReadMoreUrl: {
        en: 'https://www.cadillacarabia.com/ae-en/privacy-statement',
        ar: 'https://www.cadillacarabia.com/ae-ar/privacy-statement',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: {
        en: 'Main Menu',
        ar: 'القائمة الرئيسية',
      },
      search: {
        en: 'Search',
        ar: 'بحث',
      },
      settings: {
        en: 'Settings',
        ar: 'الإعدادات',
      },
      profile: {
        en: 'Profile',
        ar: 'الملف الشخصي',
      },
      notifications: {
        en: 'Notifications',
        ar: 'الإشعارات',
      },
      help: {
        en: 'Help & Support',
        ar: 'المساعدة والدعم',
      },
      filter: {
        en: 'Filter Options',
        ar: 'خيارات التصفية',
      },
      favorites: {
        en: 'Favorites',
        ar: 'المفضلة',
      },
      history: {
        en: 'History',
        ar: 'التاريخ',
      },
      tools: {
        en: 'Tools',
        ar: 'الأدوات',
      },
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'ألوان المقصورة',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      step: {
        en: 'Step',
        ar: 'خطوة',
      },
      openPanel: {
        en: 'Open Panel',
        ar: 'فتح اللوحة',
      },
      closePanel: {
        en: 'Close Panel',
        ar: 'إغلاق اللوحة',
      },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: {
        en: 'Ext',
        ar: 'خارجي',
      },
      int: {
        en: 'Int',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
    },
    bottomDrawer: {
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      interactions: {
        en: 'Interactions',
        ar: 'التفاعلات',
      },
      accessories: {
        en: 'Options & Accessories',
        ar: 'الإكسسوارات',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      highlights: {
        en: 'Highlights',
        ar: 'الميزات البارزة',
      },
      moreOptions: {
        en: 'More Options',
        ar: 'المزيد من الخيارات',
      },
      colorSelector: {
        en: 'Color Selector',
        ar: 'محدد اللون',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      wheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      roofs: {
        en: 'Roofs',
        ar: 'الأسقف',
      },
      performance: {
        en: 'Performance',
        ar: 'الأداء',
      },
      packages: {
        en: 'Packages',
        ar: 'الحزم',
      },
      chargingOptions: {
        en: 'Charging Options',
        ar: 'خيارات الشحن',
      },
      safetyServices: {
        en: 'Safety & Services',
        ar: 'السلامة والخدمات',
      },
      expand: {
        en: 'Expand',
        ar: 'توسيع',
      },
      minimize: {
        en: 'Minimize',
        ar: 'تصغير',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'العرض المميز',
      },
    },
    qr: {
      scan: {
        en: 'Scan QR',
        ar: 'امسح رمز الاستجابة السريعة',
      },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: {
        en: 'Share Configuration',
        ar: 'مشاركة التكوين',
      },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Cadillac Optiq configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      whatsappTitle: {
        en: 'Check out my Cadillac Optiq configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      linkedinTitle: {
        en: 'My Cadillac Optiq Configuration',
        ar: 'تكويني لسيارة شيفروليه ترافيرس',
      },
      linkedinSummary: {
        en: 'Check out my custom Cadillac Optiq configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!',
      },
      emailSubject: {
        en: 'Check out my Cadillac Optiq Configuration',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس',
      },
      emailBody: {
        en: 'I configured my dream Cadillac Optiq. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:',
      },
      linkLabel: {
        en: 'Or copy link:',
        ar: 'أو انسخ الرابط:',
      },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: {
        en: 'Copy',
        ar: 'نسخ',
      },
      saveAndShare: {
        en: 'Save & Share',
        ar: 'حفظ ومشاركة',
      },
      copyLink: {
        en: 'Copy link',
        ar: 'نسخ الرابط',
      },
      facebook: {
        en: 'Facebook',
        ar: 'Facebook',
      },
      twitter: {
        en: 'Twitter',
        ar: 'Twitter',
      },
      gmail: {
        en: 'Gmail',
        ar: 'Gmail',
      },
      whatsapp: {
        en: 'Whatsapp',
        ar: 'Whatsapp',
      },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: {
        en: 'ex showroom price',
        ar: 'سعر صالة العرض',
      },
      leads: {
        en: 'Save',
        ar: 'حفظ',
      },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      item: {
        en: 'Item',
        ar: 'العنصر',
      },
      items: {
        en: 'Items',
        ar: 'عناصر',
      },
      totalToPay: {
        en: 'Total to pay',
        ar: 'الإجمالي للدفع',
      },
      bookTestDrive: {
        en: 'Save',
        ar: 'حفظ',
      },
      requestQuote: {
        en: 'Request a Quote',
        ar: 'طلب عرض سعر',
      },
      downloadBrochure: {
        en: 'Download Brochure',
        ar: 'تحميل الكتيب',
      },
    },
    priceSummary: {
      title: {
        en: 'Price Summary',
        ar: 'ملخص السعر',
      },
      enterLocation: {
        en: 'Enter Location',
        ar: 'أدخل الموقع',
      },
      selectCountry: {
        en: 'Location',
        ar: 'الموقع',
      },
      selectCity: {
        en: 'Location',
        ar: 'الموقع',
      },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: {
        en: 'Price Breakdown',
        ar: 'تفصيل السعر',
      },
      color: {
        en: 'Color',
        ar: 'اللون',
      },
      variant: {
        en: 'Variant',
        ar: 'الفئة',
      },
      included: {
        en: 'Included',
        ar: 'مشمول',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    featureDialog: {
      playPause: {
        en: 'Play-Pause',
        ar: 'تشغيل-إيقاف',
      },
    },
    selectDealership: {
      title: {
        en: 'Select a Dealer',
        ar: 'اختر وكيلاً',
      },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      dealersInCity: {
        en: 'Dealers in {{city}}',
        ar: 'وكلاء في {{city}}',
      },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      KSA: {
        en: 'KSA',
        ar: 'السعودية',
      },
      KW: {
        en: 'Kuwait',
        ar: 'الكويت',
      },
      default: {
        en: 'UAE',
        ar: 'الإمارات',
      },
    },
    localMatches: {
      exterior: {
        en: 'Exterior',
        ar: 'خارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
      yourConfiguration: {
        en: 'Current Configuration',
        ar: 'التكوين الحالي',
      },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: {
        en: 'Your Build Details',
        ar: 'تفاصيل بنائك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      changeDealer: {
        en: 'Change Dealer',
        ar: 'تغيير الوكيل',
      },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: {
        en: 'Matches found',
        ar: 'تطابق تم العثور عليه',
      },
      startingFrom: {
        en: 'Starting from',
        ar: 'يبدأ من',
      },
      getQuote: {
        en: 'Proceed',
        ar: 'احصل على عرض سعر',
      },
      viewDifferences: {
        en: 'View Differences',
        ar: 'عرض الاختلافات',
      },
      match: {
        en: 'Match',
        ar: 'تطابق',
      },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: {
        en: 'Build Details',
        ar: 'تفاصيل التكوين',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: {
        en: 'Exact Match',
        ar: 'تطابق تام',
      },
      similarMatch: {
        en: 'Close Match',
        ar: 'تطابق قريب',
      },
      availableNow: {
        en: 'Available Now',
        ar: 'متوفر الآن',
      },
      whatYouConfigured: {
        en: 'What you configured',
        ar: 'ما قمت بتكوينه',
      },
      matchingInStock: {
        en: 'Matching vehicle available in stock',
        ar: 'مركبة مطابقة متوفرة في المخزون',
      },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: {
        en: 'Close Match Details',
        ar: 'تفاصيل التطابق القريب',
      },
      matchesYourSelection: {
        en: 'Matches your selection',
        ar: 'يطابق اختيارك',
      },
      sameAsWhatYouSelected: {
        en: 'Same as what you selected',
        ar: 'نفس ما اخترته',
      },
      replacesYourSelection: {
        en: 'Replaces your selection',
        ar: 'يستبدل اختيارك',
      },
      selectionNotIncluded: {
        en: 'Selection not included',
        ar: 'الاختيار غير مشمول',
      },
      includedInThisVehicle: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      additionalFeatureAlreadyIncluded: {
        en: 'Additional feature already included',
        ar: 'ميزة إضافية مشمولة بالفعل',
      },
      stockDisclaimer: {
        en: 'Available stock may include extra features not selected in your original configuration.',
        ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.',
      },
      additionalInstalledOptionsLegend: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      vehicleDetails: {
        en: 'Vehicle Details',
        ar: 'تفاصيل المركبة',
      },
      installedOptionsTitle: {
        en: 'Comes with vehicle',
        ar: 'يأتي مع المركبة',
      },
      additionalInstalledOptionsSubtitle: {
        en: 'Additional Installed Options:',
        ar: 'الخيارات المركبة الإضافية:',
      },
      specTrim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      specExteriorColor: {
        en: 'Exterior Color',
        ar: 'اللون الخارجي',
      },
      specInteriorColor: {
        en: 'Interior Color',
        ar: 'اللون الداخلي',
      },
      specWheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      specPackage: {
        en: 'Package',
        ar: 'الحزمة',
      },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: {
        en: 'Trim Selection',
        ar: 'اختيار الفئة',
      },
    },
    paymentPage: {
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      accessory: {
        en: 'Accessory',
        ar: 'ملحق',
      },
      closePayment: {
        en: 'Close',
        ar: 'إغلاق',
      },
    },
    packageModal: {
      includes: {
        en: 'Includes:',
        ar: 'يشمل:',
      },
      of: {
        en: 'of',
        ar: 'من',
      },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: {
        en: 'Item',
        ar: 'غرض',
      },
      items: {
        en: 'Items',
        ar: 'أغراض',
      },
      addPackage: {
        en: 'Add Package',
        ar: 'إضافة الحزمة',
      },
      noThanks: {
        en: 'No Thanks',
        ar: 'لا شكراً',
      },
      alreadyApplied: {
        en: 'Already Applied',
        ar: 'مُطبّق بالفعل',
      },
    },
    changesRequired: {
      en: 'Changes Required',
      ar: 'التغييرات المطلوبة',
    },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: {
      en: 'Apply Changes',
      ar: 'تطبيق التغييرات',
    },
    previewFeature: {
      en: 'Preview Feature',
      ar: 'معاينة الميزة',
    },
    exploreFeatures: {
      en: 'Explore Features',
      ar: 'استكشف الميزات',
    },
    buildAndPrice: {
      en: 'Build & Price',
      ar: 'البناء والسعر',
    },
    features: {
      preview: {
        en: 'Preview Feature',
        ar: 'معاينة المميزات',
      },
      playAll: {
        en: 'PLAY ALL',
        ar: 'تشغيل الكل',
      },
      title: {
        en: 'Features',
        ar: 'المميزات',
      },
    },
    header: {
      localMatches: {
        en: 'Local\nMatches',
        ar: 'التطابقات\nالمحلية',
      },
      matchingVehicles: {
        en: 'Matching vehicles',
        ar: 'مركبات مطابقة',
      },
      configurationsFound: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
    },
    configPanel: {
      playAll: {
        en: 'Play All',
        ar: 'تشغيل الكل',
      },
      pause: {
        en: 'Pause',
        ar: 'إيقاف مؤقت',
      },
      exteriorColors: {
        en: 'Exterior Colors',
        ar: 'ألوان الهيكل الخارجي',
      },
      interiorColors: {
        en: 'Interior Colors',
        ar: 'ألوان الداخلية',
      },
      roofOptions: {
        en: 'Roof Options',
        ar: 'خيارات السقف',
      },
      interiorOptions: {
        en: 'Interior Options',
        ar: 'خيارات الداخلية',
      },
      exteriorAccessories: {
        en: 'Exterior Accessories',
        ar: 'إكسسوارات خارجية',
      },
      noAdditionalOptions: {
        en: 'No additional options available for the selected trim.',
        ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.',
      },
      noFilters: {
        en: 'No filters available.',
        ar: 'لا توجد فلاتر متاحة.',
      },
      noVariantsMatch: {
        en: 'No trims match the selected filters.',
        ar: 'لا توجد فئات تطابق الفلاتر المختارة.',
      },
      configurationBreakdown: {
        en: 'Configuration Breakdown',
        ar: 'تفاصيل التكوين',
      },
      yourConfiguration: {
        en: 'Your Configuration',
        ar: 'تكوينك',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    conflictDialog: {
      description: {
        en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.',
        ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.',
      },
    },
    viewport: {
      changeBackground: {
        en: 'Change Background',
        ar: 'تغيير الخلفية',
      },
      selectLanguage: {
        en: 'Switch Language (عربي)',
        ar: 'تبديل اللغة (EN)',
      },
      menu: {
        en: 'Menu',
        ar: 'القائمة',
      },
      exploreFeatures: {
        en: 'Explore Features',
        ar: 'استكشف الميزات',
      },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: '',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: '',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - المنطقة الصناعية 5، شارع الصناعية 1، طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: '',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الكندي للسيارات - منطقة المدهب، شارع محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: '',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية، طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: '',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - صالة عرض ديرة، شارع الاتحاد، الخبيصي، دبي',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: '',
        dealerId: '321719',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - City Walk Boulevard - Happiness Street, Dubai',
          ar: 'الكندي للسيارات - سيتي ووك، الوصل، شارع السعادة، دبي',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: '',
        dealerId: '311027',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Umm Al Nar, Al Ain Road, Abu Dhabi',
          ar: 'الكندي للسيارات - أم النار، طريق العين، أبوظبي',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: '',
        dealerId: '315694',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Al Waha Building, Al Ain',
          ar: 'الكندي للسيارات - شارع نهيان الأول، مبنى الواحة، قطعة 6، العين',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: '',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية، طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: '',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - طريق الملك عبدالله، حائل',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: '',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب، طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: '',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - طريق الملك عبدالعزيز، بريدة، القصيم',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: '',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
      {
        id: 5,
        country: 'KSA',
        value: '',
        dealerId: '318928',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - King Fahad Road, Dammam',
          ar: 'الجميح للسيارات - طريق الملك فهد، الدمام',
        },
      },
      {
        id: 6,
        country: 'KSA',
        value: '',
        dealerId: '213142',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - Rawdah Khurais Road, Riyadh',
          ar: 'الجميح للسيارات - طريق خريص الفرعي، الريان، الرياض',
        },
      },
      {
        id: 7,
        country: 'KSA',
        value: '',
        dealerId: '307267',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - Malibari Al Madina Road, Jeddah',
          ar: 'الجميح للسيارات - ماليباري سكوير، الروضة، طريق المدينة، جدة',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: '',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الأمانة للسيارات - الري، الطريق الدائري الرابع، الكويت',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: '',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الأمانة للسيارات - المنطقة الصناعية، شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م - القرية 475، طريق الشيخ جابر الصباح',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، قرب المتحف الوطني، بيروت',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '259952',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'المؤسسة التجارية العمانية ش.م.م - شارع غلا المعارض',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '122234',
        dealerName: 'Mannai Trading Company',
        label: {
          en: 'Mannai Trading Company W.L.L., Salwa E Industrial Street',
          ar: 'شركة المناعي للسيارات ذ.م.م - شارع سلوى الصناعي',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة - الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: '',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: '',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - المنطقة الصناعية 5، شارع الصناعية 1، طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: '',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الكندي للسيارات - منطقة المدهب، شارع محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: '',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية، طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: '',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - صالة عرض ديرة، شارع الاتحاد، الخبيصي، دبي',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: '',
        dealerId: '321719',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - City Walk Boulevard - Happiness Street, Dubai',
          ar: 'الكندي للسيارات - سيتي ووك، الوصل، شارع السعادة، دبي',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: '',
        dealerId: '311027',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Umm Al Nar, Al Ain Road, Abu Dhabi',
          ar: 'الكندي للسيارات - أم النار، طريق العين، أبوظبي',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: '',
        dealerId: '315694',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Al Waha Building, Al Ain',
          ar: 'الكندي للسيارات - شارع نهيان الأول، مبنى الواحة، قطعة 6، العين',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: false,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: false,
    showDisplayCarPrice: false, // Cadillac: no MSRP anywhere in the UI
    showCart: false,
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'optiq',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Optiq',
    modelName: 'Optiq',
    pageTitle: 'Optiq Configurator',
    client: 'GM',
    brand: 'cadillac',
    modelId: 'CAOPTIQ',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      provider: 'adobe',
      brand: 'cadillac',
      modelName: 'optiq',
      vehicleType: 'suv',
    },
    presentation: {
      video: 'videos/2366/presentation_video_1.webm',
      loadingImage: 'images/2366/sliderImage.webp',
      transitionImage: 'images/2366/transition_logo.png',
      headerLogo: 'images/2366/transition_logo.png',
      headerLogoKeepColor: true,
      headerLogoInvertInLight: true,
      // Cadillac's crest reads too small at the shared default header-logo size.
      headerLogoLarge: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: '',
          },
          value: {
            en: '315 HP',
            ar: '315 حصان',
          },
          unit: '',
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران',
          },
          value: {
            en: '450 NM',
            ar: '450 نيوتن متر',
          },
          unit: '',
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'Range',
            ar: 'المدى',
          },
          value: {
            en: '511 KM',
            ar: '511 كم',
          },
          unit: '',
        },
      ],
    },
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: [
          'email',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: [
          'whatsapp',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [
    {
      id: 'modelyear',
      key: 'modelyear',
      label: {
        en: 'Model Year',
        ar: 'سنة الطراز',
      },
      disabled: false,
      type: 'chip',
      options: [
        {
          id: '2025',
          label: {
            en: '2025',
            ar: '2025',
          },
          value: '2025',
          checked: false,
        },
        {
          id: '2026',
          label: {
            en: '2026',
            ar: '2026',
          },
          value: '2026',
          checked: true,
        },
      ],
    },
  ],
  styleConfig: {
    cssVariables: {
      '--default-font-family': '\'Cadillac Gothic\', \'Chevy Sans\', sans-serif',
      '--bold-font-family': '\'Cadillac Gothic\', \'Chevy Sans Medium\', sans-serif',
      '--brand-primary': '#1a1971',
      '--brand-primary-hover': '#15146a',
      '--brand-secondary': '#191897',
      '--brand-accent': '#1a1971',
      '--brand-accent-hover': '#15146a',
      '--brand-accent-light': '#e8e8f0',
      '--brand-surface': '#f3f3f3',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e6e6e6',
      '--brand-border-selected': '#1a1971',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#1a1971',
      '--brand-toggle-gradient-to': '#191897',
      '--brand-toggle-knob-border': '#1a1971',
      '--brand-accent-secondary': '#1a1971',
      '--brand-card-radius': '0px',
      '--brand-card-radius-sm': '0px',
      '--brand-card-radius-lg': '0px',
      '--brand-button-radius': '0px',
      '--brand-button-radius-sm': '0px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '0px',
      // Enter-CTA fill: white with the Cadillac blue chevron, rather than
      // the default #0072ce block. Shadow keeps the label legible on it.
      '--brand-cta-arrow-bg': '#ffffff',
      '--brand-cta-arrow-fg': '#1a1971',
      '--brand-cta-shimmer': '#ffffff',
      '--enter-configurator-text-shadow': '0px 0px 12px #000000',
      '--brand-button-border': '#ffffff',
      '--brand-dialog-radius': '0px',
      '--brand-dialog-option-radius': '0px',
    },
  },
} as unknown as UIMetadataComplete;
